import React, { useCallback, useEffect, useRef, useState } from "react";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import config from "../../../config";
import { getDefaultAccessKeys } from "../../../services/app/app-access-key-service";

export default function WebChatPreview({ appId, settings }) {
  const frame = useRef(null);
  const pointerOutside = useRef(false);
  const latestSettings = useRef(settings);
  latestSettings.current = settings;
  const [open, setOpen] = useState(false);
  // Size reported by the text bar launcher; null while showing the bubble.
  const [barSize, setBarSize] = useState(null);
  const [mobile, setMobile] = useState(false);
  // Full screen only restyles this section, so the iframe (and any test
  // conversation in it) is never reloaded.
  const [fullScreen, setFullScreen] = useState(false);
  const [connection, setConnection] = useState("loading");
  const [reload, setReload] = useState(0);
  // The widget needs the app's key to chat. It has no cookie inside the
  // platform, so the key travels with the settings message (never the URL).
  const accessKey = useRef(null);
  const [keyState, setKeyState] = useState("loading");
  const src = config.WEB_PLUGIN_URL
    ? `${config.WEB_PLUGIN_URL.replace(/\/$/, "")}/?app_id=${encodeURIComponent(appId)}&plugin=1&preview=1&test_mode=1&skip_greeting=1`
    : "";
  const origin = src ? new URL(src, window.location.href).origin : "";
  const sendSettings = useCallback(() => {
    if (origin) frame.current?.contentWindow?.postMessage({
      type: "botcircuits_preview_settings", settings: latestSettings.current,
      ...(accessKey.current ? { accessKey: accessKey.current } : {}),
    }, origin);
  }, [origin]);

  useEffect(() => {
    const onPointerMove = event => {
      if (!origin || !barSize || pointerOutside.current || !frame.current) return;
      const bounds = frame.current.getBoundingClientRect();
      if (event.clientX >= bounds.left && event.clientX <= bounds.right
          && event.clientY >= bounds.top && event.clientY <= bounds.bottom) return;
      pointerOutside.current = true;
      frame.current.contentWindow?.postMessage({ type: 'botcircuits_pointer_leave' }, origin);
    };
    window.addEventListener('pointermove', onPointerMove, true);
    return () => window.removeEventListener('pointermove', onPointerMove, true);
  }, [origin, barSize]);

  useEffect(() => {
    let active = true;
    accessKey.current = null;
    setKeyState("loading");
    getDefaultAccessKeys(appId).then(response => {
      if (!active) return;
      accessKey.current = response?.accessKey || null;
      setKeyState(accessKey.current ? "ready" : "failed");
    });
    return () => { active = false; };
  }, [appId]);

  useEffect(() => { sendSettings(); }, [settings, keyState, sendSettings]);
  useEffect(() => {
    if (!fullScreen) return undefined;
    const onKeyDown = event => { if (event.key === "Escape") setFullScreen(false); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [fullScreen]);
  useEffect(() => {
    setConnection("loading");
    setOpen(false);
    setBarSize(null);
    // Retry the handshake: iframe load and React's message listener may occur
    // in either order. Only the plugin's acknowledgement marks it connected.
    const retry = window.setInterval(sendSettings, 500);
    const timeout = window.setTimeout(() => {
      setConnection("failed");
      window.clearInterval(retry);
    }, 10000);
    const receive = event => {
      if (!origin || event.origin !== origin || event.source !== frame.current?.contentWindow) return;
      if (event.data?.type === 'botcircuits_pointer_enter') pointerOutside.current = false;
      if (event.data?.type === "botcircuits_preview_ready") sendSettings();
      if (event.data?.type === "botcircuits_preview_applied") {
        setConnection("ready");
        if (typeof event.data.isOpen === "boolean") setOpen(event.data.isOpen);
        window.clearInterval(retry);
        window.clearTimeout(timeout);
      }
      if (["botcircuits_toggle_state", "botcircuits_widget_layout"].includes(event.data?.type)
          && typeof event.data.isOpen === "boolean") setOpen(event.data.isOpen);
      if (event.data?.type === "botcircuits_widget_layout") {
        const { launcherStyle, width, height } = event.data;
        setBarSize(launcherStyle === "text-bar" && width > 0 && height > 0 ? { width, height } : null);
      }
    };
    window.addEventListener("message", receive);
    sendSettings();
    return () => {
      window.removeEventListener("message", receive);
      window.clearInterval(retry);
      window.clearTimeout(timeout);
    };
  }, [src, origin, reload, sendSettings]);

  // Same rules as the SDK: the text bar's transparent frame includes a shadow
  // gutter and sits closer to the edge; the bubble sizes from its open state.
  const edge = barSize ? 4 : 16;
  const centered = settings.position === "bottom-center";
  const placement = {
    position: "absolute", bottom: edge,
    right: centered ? "50%" : edge,
    transform: centered ? "translateX(50%)" : undefined,
  };
  const frameSize = barSize ? {
    width: barSize.width, maxWidth: "calc(100% - 8px)", height: barSize.height, maxHeight: "calc(100% - 8px)",
    borderRadius: 0, boxShadow: undefined,
  } : {
    width: open ? "min(399px, calc(100% - 32px))" : 64, height: open ? 580 : 64,
    borderRadius: open ? 16 : "50%",
    boxShadow: connection === "ready" && open ? "0 12px 36px #0f172a26" : undefined,
  };
  return (
    <section role={fullScreen ? "dialog" : undefined} aria-modal={fullScreen || undefined} aria-label={fullScreen ? "Website preview" : undefined}
      className={fullScreen
        ? "fixed inset-0 z-[1000] flex flex-col bg-gray-100 p-4 sm:p-6"
        : "flex min-w-0 flex-col rounded-2xl border border-gray-200 bg-gray-50/70 p-4 sm:p-5 xl:h-full xl:min-h-0"}>
      <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900">Website preview</h2>
        <div className="flex items-center rounded-lg border border-gray-200 bg-white p-1">
          <div role="group" aria-label="Preview device" className="flex">
            {[false, true].map(isMobile => <button key={String(isMobile)} type="button" onClick={() => setMobile(isMobile)} aria-pressed={mobile === isMobile}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition focus-visible:ring-2 focus-visible:ring-primary ${mobile === isMobile ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-900"}`}>
              {isMobile ? "Mobile" : "Desktop"}
            </button>)}
          </div>
          <span aria-hidden="true" className="mx-1 h-5 w-px bg-gray-200" />
          <button type="button" onClick={() => setFullScreen(value => !value)} aria-pressed={fullScreen}
            aria-label={fullScreen ? "Exit full screen" : "View full screen"} title={fullScreen ? "Exit full screen (Esc)" : "View full screen"}
            className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition focus-visible:ring-2 focus-visible:ring-primary ${fullScreen ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-900"}`}>
            {fullScreen ? <MdFullscreenExit size={18} aria-hidden="true" /> : <MdFullscreen size={18} aria-hidden="true" />}
          </button>
        </div>
      </div>
      <div className={`mx-auto flex w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all ${fullScreen ? "min-h-0 flex-1" : "xl:min-h-0 xl:flex-1"}`}
        style={{ maxWidth: mobile ? 375 : "100%" }}>
        <div className="flex shrink-0 items-center gap-2 border-b bg-gray-50 px-4 py-3">
          <span className="h-2 w-2 rounded-full bg-red-300" /><span className="h-2 w-2 rounded-full bg-amber-300" />
          <span className="h-2 w-2 rounded-full bg-green-300" />
          <span className="ml-3 text-xs text-gray-400">your-website.com</span>
        </div>
        <div className={`relative overflow-hidden bg-slate-50 ${fullScreen ? "min-h-0 flex-1" : "h-[620px] xl:h-auto xl:min-h-[480px] xl:flex-1"}`}>
          <div aria-hidden="true" className="p-8 text-slate-800">
            <div className="flex justify-between text-sm font-semibold"><span>Your brand</span><span>Menu ☰</span></div>
            <div className="mt-16 text-xs uppercase tracking-widest text-indigo-500">Welcome to your website</div>
            <div className="mt-4 max-w-xs text-4xl font-semibold leading-tight">A little help.<br />Right here.</div>
            <p className="mt-5 max-w-xs text-sm leading-6 text-slate-400">Give every visitor a direct line to your business.</p>
            <div className="mt-7 inline-block rounded-lg bg-slate-900 px-5 py-3 text-sm text-white">Explore more</div>
            <div className="mt-12 grid grid-cols-3 gap-4">{[1, 2, 3].map(item => <div key={item} className="h-28 rounded-xl bg-slate-200/60" />)}</div>
          </div>
          {src && <iframe key={reload} ref={frame} title="Web chat website preview" src={src}
            onLoad={sendSettings} onError={() => setConnection("failed")}
            style={{ ...placement, ...frameSize, border: 0,
              background: "transparent", display: "block", zIndex: 10,
              visibility: connection === "ready" ? "visible" : "hidden",
              pointerEvents: connection === "ready" ? "auto" : "none",
              overflow: "hidden", colorScheme: "light" }} />}

        </div>
      </div>
      <div className="mt-4 flex shrink-0 flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
        <span role="status" className="inline-flex items-center gap-2"><span className={`h-1.5 w-1.5 rounded-full ${connection === "ready" ? "bg-emerald-500" : "bg-amber-500"}`} />
          {connection === "ready" ? "Live widget preview" : connection === "failed" || !src ? "Widget unavailable" : "Connecting to widget…"}</span>
        <span>{settings.launcherStyle === "text-bar" ? "Text bar" : "Chat bubble"} · {centered ? "Bottom center" : "Bottom right"} · {open ? "Widget open" : "Widget minimized"}</span>
      </div>
      {keyState === "failed" && connection === "ready" && <p className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-600">
        Chat is unavailable in this preview because the app's access key could not be loaded. Appearance changes still preview here.
      </p>}
      {(!src || connection === "failed") && <div role="alert" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <p>{src ? "The widget has not connected. Check that the updated web plugin is running at the configured URL." : "Set the web plugin URL to load the widget preview."}</p>
        {src && <button type="button" onClick={() => setReload(value => value + 1)} className="mt-2 font-semibold underline">Retry preview</button>}
      </div>}
    </section>
  );
}
